import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  FiLoader,
  FiSave,
  FiUsers,
} from "react-icons/fi";

import {
  getAssignableCollaborators,
  getContainerCollaborators,
  replaceContainerCollaborators,
} from "../services/containerApi";

const ASSIGNABLE_CACHE_MS = 60 * 1000;

let assignableUsersCache = {
  data: null,
  expiresAt: 0,
  promise: null,
};

const containerAccessRequests = new Map();

const buildSelectedMap = (assigned = []) => {
  const next = {};

  assigned.forEach((item) => {
    next[item.userId] = {
      selected: true,
      canUpdateStatus:
        item.canUpdateStatus !== false,
      canUploadDocuments:
        item.canUploadDocuments !== false,
    };
  });

  return next;
};

const mergeUsers = (
  available = [],
  assigned = []
) => {
  const map = new Map();

  available.forEach((user) => {
    if (user?.id !== undefined) {
      map.set(String(user.id), user);
    }
  });

  assigned.forEach((item) => {
    const user = item?.user;

    if (
      user?.id !== undefined &&
      !map.has(String(user.id))
    ) {
      map.set(String(user.id), {
        id: user.id,
        name: user.name,
        email: user.email,
        phone: user.phone,
        roleName: user.roleName,
        accessScope: "assigned",
        isActive: user.isActive,
      });
    }
  });

  return Array.from(map.values());
};

const loadAssignableUsers = async (
  force = false
) => {
  const now = Date.now();

  if (
    !force &&
    Array.isArray(assignableUsersCache.data) &&
    assignableUsersCache.expiresAt > now
  ) {
    return assignableUsersCache.data;
  }

  if (assignableUsersCache.promise) {
    return assignableUsersCache.promise;
  }

  const request = getAssignableCollaborators()
    .then((response) => {
      const users = Array.isArray(
        response.data?.users
      )
        ? response.data.users
        : [];

      assignableUsersCache = {
        data: users,
        expiresAt:
          Date.now() + ASSIGNABLE_CACHE_MS,
        promise: null,
      };

      return users;
    })
    .catch((error) => {
      assignableUsersCache.promise = null;
      throw error;
    });

  assignableUsersCache.promise = request;

  return request;
};

const loadContainerAccess = (
  containerId
) => {
  const key = String(containerId);

  if (containerAccessRequests.has(key)) {
    return containerAccessRequests.get(key);
  }

  const request = getContainerCollaborators(
    containerId
  )
    .then((response) =>
      Array.isArray(
        response.data?.collaborators
      )
        ? response.data.collaborators
        : []
    )
    .finally(() => {
      containerAccessRequests.delete(key);
    });

  containerAccessRequests.set(key, request);

  return request;
};

const getLoadErrorMessage = (error) => {
  if (error.response?.status === 404) {
    return (
      "Container collaborator routes are " +
      "missing from the backend."
    );
  }

  return (
    error.response?.data?.message ||
    "Unable to load container access"
  );
};

export default function ContainerCollaboratorsPanel({
  containerId,
  canManage,
  onMessage,
}) {
  const onMessageRef = useRef(onMessage);
  const mountedRef = useRef(false);

  const [users, setUsers] = useState([]);
  const [selected, setSelected] =
    useState({});
  const [loading, setLoading] =
    useState(false);
  const [loaded, setLoaded] =
    useState(false);
  const [saving, setSaving] =
    useState(false);
  const [dirty, setDirty] =
    useState(false);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  const notify = useCallback(
    (message, type) => {
      onMessageRef.current?.(
        message,
        type
      );
    },
    []
  );

  const load = useCallback(
    async ({
      forceAssignableUsers = false,
    } = {}) => {
      if (!canManage || !containerId) {
        if (mountedRef.current) {
          setUsers([]);
          setSelected({});
          setLoaded(true);
          setDirty(false);
        }

        return;
      }

      if (mountedRef.current) {
        setLoading(true);
      }

      try {
        const [
          available,
          assigned,
        ] = await Promise.all([
          loadAssignableUsers(
            forceAssignableUsers
          ),
          loadContainerAccess(
            containerId
          ),
        ]);

        if (!mountedRef.current) {
          return;
        }

        setUsers(
          mergeUsers(
            available,
            assigned
          )
        );

        setSelected(
          buildSelectedMap(assigned)
        );

        setDirty(false);
      } catch (error) {
        if (!mountedRef.current) {
          return;
        }

        setUsers([]);
        setSelected({});

        notify(
          getLoadErrorMessage(error),
          "error"
        );
      } finally {
        if (mountedRef.current) {
          setLoading(false);
          setLoaded(true);
        }
      }
    },
    [
      canManage,
      containerId,
      notify,
    ]
  );

  useEffect(() => {
    mountedRef.current = true;
    load();

    return () => {
      mountedRef.current = false;
    };
  }, [load]);

  const toggleUser = (
    userId,
    checked
  ) => {
    setSelected((previous) => ({
      ...previous,
      [userId]: checked
        ? {
            selected: true,
            canUpdateStatus:
              previous[userId]
                ?.canUpdateStatus ??
              true,
            canUploadDocuments:
              previous[userId]
                ?.canUploadDocuments ??
              true,
          }
        : {
            selected: false,
            canUpdateStatus: true,
            canUploadDocuments: true,
          },
    }));

    setDirty(true);
  };

  const changePermission = (
    userId,
    key,
    value
  ) => {
    setSelected((previous) => ({
      ...previous,
      [userId]: {
        selected: true,
        canUpdateStatus:
          previous[userId]
            ?.canUpdateStatus ??
          true,
        canUploadDocuments:
          previous[userId]
            ?.canUploadDocuments ??
          true,
        [key]: value,
      },
    }));

    setDirty(true);
  };

  const save = async () => {
    if (
      !containerId ||
      !dirty ||
      saving
    ) {
      return;
    }

    const collaborators =
      Object.entries(selected)
        .filter(
          ([, value]) =>
            value.selected
        )
        .map(
          ([userId, value]) => ({
            userId: Number(userId),
            canUpdateStatus: Boolean(
              value.canUpdateStatus
            ),
            canUploadDocuments:
              Boolean(
                value.canUploadDocuments
              ),
          })
        );

    setSaving(true);

    try {
      await replaceContainerCollaborators(
        containerId,
        collaborators
      );

      notify(
        "Container access updated successfully"
      );

      await load({
        forceAssignableUsers: true,
      });
    } catch (error) {
      notify(
        error.response?.data?.message ||
          "Unable to update container access",
        "error"
      );
    } finally {
      if (mountedRef.current) {
        setSaving(false);
      }
    }
  };

  if (!canManage) {
    return null;
  }

  /*
   * Keep the interface clean when there are no
   * assigned-only users to display.
   */
  if (
    !loaded ||
    loading ||
    users.length === 0
  ) {
    return null;
  }

  const assignedCount =
    Object.values(selected).filter(
      (value) => value.selected
    ).length;

  return (
    <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-violet-50 text-violet-600 dark:bg-violet-950 dark:text-violet-300">
            <FiUsers />
          </div>

          <div>
            <h4 className="font-bold text-slate-950 dark:text-white">
              Container Access
            </h4>

            <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Assign supplier or forwarder
              accounts to this container.
            </p>
          </div>
        </div>

        <span className="w-fit rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          {assignedCount} assigned
        </span>
      </div>

      <div className="divide-y divide-slate-100 dark:divide-slate-800">
        {users.map((user) => {
          const value =
            selected[user.id] || {};

          return (
            <div
              key={user.id}
              className="grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_auto_auto] md:items-center"
            >
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={Boolean(
                    value.selected
                  )}
                  onChange={(event) =>
                    toggleUser(
                      user.id,
                      event.target.checked
                    )
                  }
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                />

                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-slate-900 dark:text-white">
                    {user.name ||
                      user.email ||
                      `User #${user.id}`}
                  </span>

                  <span className="mt-1 block truncate text-xs text-slate-500 dark:text-slate-400">
                    {user.roleName ||
                      "External User"}
                    {user.email
                      ? ` â€¢ ${user.email}`
                      : ""}
                  </span>
                </span>
              </label>

              <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  disabled={
                    !value.selected
                  }
                  checked={Boolean(
                    value.selected &&
                      value.canUpdateStatus
                  )}
                  onChange={(event) =>
                    changePermission(
                      user.id,
                      "canUpdateStatus",
                      event.target.checked
                    )
                  }
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                />

                Update progress
              </label>

              <label className="flex items-center gap-2 text-xs font-semibold text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  disabled={
                    !value.selected
                  }
                  checked={Boolean(
                    value.selected &&
                      value.canUploadDocuments
                  )}
                  onChange={(event) =>
                    changePermission(
                      user.id,
                      "canUploadDocuments",
                      event.target.checked
                    )
                  }
                  className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
                />

                Upload documents
              </label>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end border-t border-slate-100 p-4 dark:border-slate-800">
        <button
          type="button"
          disabled={
            saving || !dirty
          }
          onClick={save}
          className="inline-flex items-center gap-2 rounded-xl bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? (
            <FiLoader className="animate-spin" />
          ) : (
            <FiSave />
          )}

          {saving
            ? "Saving..."
            : "Save Access"}
        </button>
      </div>
    </section>
  );
}
